"use client";

/**
 * Hero.tsx
 *
 * Full-screen hero section at the top of the Royal Blue homepage.
 * Features a background image, animated heading, subtitle, and two CTA buttons.
 *
 * All content (title, subtitle, buttons, background) is driven by the CMS.
 * If a field is missing we use the branded fallback values defined below.
 */

import React from "react";
import { useAppSelector } from "@/store/hooks";
import { motion } from "framer-motion";
import Link from "next/link";

// ---------------------------------------------------------------------------
// Fallbacks — shown when the CMS hasn't provided values yet
// ---------------------------------------------------------------------------

const DEFAULTS = {
  title: "Offering The Best Tasting Experience!",
  subtitle: "Fresh Ingredient, Tasty Meals, And Creative Chefs",
  backgroundImage:
    "https://images.unsplash.com/photo-1514361892635-6b07e31e75f9?auto=format&fit=crop&w=1920&q=80",
  primaryButtonLabel: "Book A Table",
  primaryButtonHref: "#reservation",
  secondaryButtonLabel: "Book An Event",
  secondaryButtonHref: "/events",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Hero = () => {
  const { data: content } = useAppSelector((state: any) => state.content);

  // The CMS stores hero data under "royalBlueHero" (specific) or "hero" (generic)
  const data = content?.royalBlueHero || content?.hero;

  const title = data?.title || DEFAULTS.title;
  const subtitle = data?.subtitle || DEFAULTS.subtitle;
  const backgroundImage = data?.backgroundImage || DEFAULTS.backgroundImage;

  // Buttons — support both the flat "label/href" shape and the older "primaryButtonText" shape
  const primaryLabel =
    data?.primaryButton?.label ||
    data?.primaryButtonText ||
    DEFAULTS.primaryButtonLabel;
  const primaryHref = data?.primaryButton?.href || DEFAULTS.primaryButtonHref;

  const secondaryLabel =
    data?.secondaryButton?.label ||
    data?.secondaryButtonText ||
    DEFAULTS.secondaryButtonLabel;
  const secondaryHref =
    data?.secondaryButton?.href || DEFAULTS.secondaryButtonHref;

  return (
    <section
      id="home"
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image with a subtle zoom animation via CSS */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 scale-105"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
      >
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Centred content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-cormorant text-5xl md:text-8xl text-white mb-6 font-bold leading-tight"
        >
          {title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-playfair italic font-normal text-2xl md:text-3xl text-white mb-8 drop-shadow-lg"
        >
          {subtitle}
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6"
        >
          <Link href={primaryHref}>
            <button className="px-10 py-4 bg-[#c59d5f] hover:bg-[#b08b53] text-white font-bold tracking-widest transition-all duration-300">
              {primaryLabel}
            </button>
          </Link>
          <Link href={secondaryHref}>
            <button className="px-10 py-4 border-2 border-white text-white hover:bg-white hover:text-black font-bold tracking-widest transition-all duration-300">
              {secondaryLabel}
            </button>
          </Link>
        </motion.div>
      </div>

      {/* Animated scroll indicator at the bottom */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-white"
      >
        <div className="w-[1px] h-12 bg-white/50 mb-4" />
        <span className="text-[10px] tracking-widest uppercase mb-4">
          Scroll Down
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
